<script setup lang="ts">
import { nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { AssetContainerType } from '@nova/core';

import type { AssetContainerData } from '@/composables/useCheckinFlow';

defineOptions({ name: 'AssetDetailsForm' });

const { assetContainerData, maxContainerData = 3 } = defineProps<{
  assetContainerData: AssetContainerData[];
  assetContainerTypes: Array<{ title: string; value: AssetContainerType }>;
  maxContainerData?: number;
}>();

const emit = defineEmits<{
  'update:assetContainerData': [value: AssetContainerData[]];
}>();

const { t } = useI18n();

function requiredValidator(value: string, label: string): string | true {
  return !value || value.length === 0
    ? t('common.validation.fieldRequiredWithLabel', { label })
    : true;
}

function updateContainer(index: number, patch: Partial<AssetContainerData>): void {
  emit(
    'update:assetContainerData',
    assetContainerData.map((container, position) =>
      position === index ? { ...container, ...patch } : container
    )
  );
}

function handleRemove(index: number): void {
  emit(
    'update:assetContainerData',
    assetContainerData.filter((_, position) => position !== index)
  );
}

function handleAdd(): void {
  const newData = [
    ...assetContainerData,
    { containerCode: '', containerType: AssetContainerType.Trailer }
  ];

  emit('update:assetContainerData', newData);

  nextTick(() => {
    const lastContainerType = document.getElementById(
      `asset-visit-form-container-type-${newData.length - 1}`
    );
    lastContainerType?.focus();
    lastContainerType?.scrollIntoView({ behavior: 'smooth' });
  });
}
</script>

<template>
  <div class="d-flex flex-column gap-6">
    <div
      v-for="(container, index) of assetContainerData"
      :key="index"
      class="d-flex flex-column gap-6">
      <div class="custom-field comet-i18n-exclude">
        <label
          class="custom-field__label required"
          :for="`asset-visit-form-container-type-${index}`">
          {{ t('assetDetails.assetType') }}
        </label>
        <v-select
          :id="`asset-visit-form-container-type-${index}`"
          :data-testid="`asset-visit-form-container-type-${index}`"
          variant="outlined"
          color="primary"
          hide-details
          item-title="title"
          item-value="value"
          class="flex-grow-1"
          :items="assetContainerTypes"
          :placeholder="t('assetDetails.assetTypePlaceholder')"
          :model-value="container.containerType"
          :rules="[requiredValidator(container.containerType, t('assetDetails.trailerType'))]"
          @update:model-value="updateContainer(index, { containerType: $event })"></v-select>
      </div>

      <div class="custom-field comet-i18n-exclude">
        <label
          class="custom-field__label required"
          :for="`asset-visit-form-container-code-${index}`">
          {{ t('assetDetails.assetId') }}
        </label>
        <v-text-field
          :id="`asset-visit-form-container-code-${index}`"
          :data-testid="`asset-visit-form-container-code-${index}`"
          variant="outlined"
          color="primary"
          hide-details
          required
          maxlength="50"
          class="flex-grow-1"
          :placeholder="t('assetDetails.assetIdPlaceholder')"
          :model-value="container.containerCode"
          :rules="[requiredValidator(container.containerCode, t('assetDetails.trailerId'))]"
          @update:model-value="updateContainer(index, { containerCode: $event })"></v-text-field>
      </div>

      <v-btn
        v-if="assetContainerData.length > 1 && index > 0"
        variant="text"
        color="error"
        class="comet-i18n-exclude align-self-end"
        @click="handleRemove(index)">
        {{ t('assetDetails.remove') }}
      </v-btn>
    </div>

    <v-btn
      v-if="assetContainerData.length < maxContainerData"
      block
      variant="outlined"
      class="comet-i18n-exclude"
      prepend-icon="mdi-plus"
      @click="handleAdd">
      {{ t('assetDetails.addAnother') }}
    </v-btn>
  </div>
</template>
